import React from 'react';
import { Activity, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { useLoading } from '../hooks/useLoading';
import { sleep } from '../utils/sleep';
import { 
  statusMetrics, 
  securityEvents, 
  systemMetrics, 
  activeMonitors 
} from '../data/monitoring';

const Monitoring = () => {
  const { isLoading, withLoading } = useLoading();

  const handleRefresh = async () => {
    await withLoading(async () => {
      await sleep(3000);
      console.log('Refreshing monitoring data...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Refreshing data..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Activity className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Attack Surface Monitoring</h1>
            <p className="text-sm text-gray-400">Real-time external attack surface visibility</p>
          </div>
        </div>
        <Button onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-4 gap-6">
        {statusMetrics.map((metric) => (
          <div key={metric.label} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">{metric.label}</span>
              <span className={`text-xs ${metric.status === 'success' ? 'text-emerald-500' : 'text-yellow-500'}`}>
                {metric.trend}
              </span>
            </div>
            <div className="text-2xl font-bold text-white">{metric.value}</div>
          </div>
        ))}
      </div>

      {/* Live Monitoring */}
      <div className="grid grid-cols-2 gap-6">
        {/* Security Events */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Surface Findings</h2>
            <span className="text-sm text-gray-400">Last 24 hours</span>
          </div>
          <div className="space-y-4">
            {securityEvents.map((event) => {
              const Icon = event.icon;
              return (
                <div key={event.id} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon className={`h-5 w-5 ${
                      event.severity === 'high' ? 'text-red-500' :
                      event.severity === 'medium' ? 'text-yellow-500' :
                      'text-emerald-500'
                    }`} />
                    <div>
                      <div className="text-sm font-medium text-white">{event.title}</div>
                      <div className="text-xs text-gray-400 mt-1">{event.description}</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">{event.timestamp}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* System Health */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Asset Health</h2>
            <span className="text-sm text-gray-400">Real-time</span>
          </div>
          <div className="space-y-4">
            {systemMetrics.map((metric) => (
              <div key={metric.name} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">{metric.name}</span>
                  <span className={`text-sm ${
                    metric.status === 'normal' ? 'text-emerald-500' : 'text-yellow-500'
                  }`}>{metric.value}%</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      metric.status === 'normal' ? 'bg-emerald-500' : 'bg-yellow-500'
                    }`}
                    style={{ width: `${metric.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Active Monitors */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Active Monitors</h2>
        <div className="grid grid-cols-3 gap-6">
          {activeMonitors.map((monitor) => (
            <div key={monitor.name} className="p-4 bg-gray-800/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-white">{monitor.name}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  monitor.status === 'healthy' 
                    ? 'bg-emerald-500/10 text-emerald-500'
                    : 'bg-yellow-500/10 text-yellow-500'
                }`}>
                  {monitor.status}
                </span>
              </div>
              <div className="text-2xl font-bold text-white">{monitor.count}</div>
              <div className="text-xs text-gray-400 mt-1">Assets monitored</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Monitoring;